import React, { useEffect, useState } from "react";
import { Button, Box, TextField, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactPaginate from "react-paginate";

import { DELETED } from "../../../../constant/constant";
import { BaseModal } from "../../../Modal/BaseModal";
import DeleteModal from "../../../common/DeleteModal";
import { PlanSchema } from "../../../../schema/schema";
import { apiCall } from "../../../../API/apiCall";

import Loader from "../../../loader/Loader";
import { toast } from "react-toastify";
import DataTable from "../../../common/DataTable";
import ModalFormPlan from "./ModalFormPlan";
import { adminInstitutePlanColumns } from "../../../../utils/tableConfig";
import useDebounce from "../../../../hook/useDebounce";

const PlanList = () => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState({ isLoading: false, type: "" });

  const [formError, setFormError] = useState({});

  const [plansData, setPlansData] = useState([]);

  const [modalState, setModalState] = useState({
    type: null,
    open: false,
    formData: {
      id: null,
      name: "",
      frequency: "",
      pricePerQty: "",
      minimumQty: "",
      planType: "",
    },
    originalData: {
      id: null,
      name: "",
      frequency: "",
      pricePerQty: "",
      minimumQty: "",
      planType: "",
    },
    changedFields: {},
  });

  const [search, setSearch] = useState("");
  const debounceValue = useDebounce(search.trim(), 1000);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = searchParams.get("currentPage") || 1;
  const pageLimit = 5;

  const [paginationData, setPaginationData] = useState({
    totalCount: 0,
    currentPage: 0,
    totalPages: 0,
    previousPage: null,
    nextPage: null,
  });

  useEffect(() => {
    setLoading({ isLoading: true, type: "getAllPlan" });
    getAllPlans(Number(currentPage), pageLimit);
  }, [currentPage, debounceValue]);

  const getAllPlans = async (page, limit) => {
    try {
      const response = await apiCall({
        url: "/institute/plan",
        method: "GET",
        params: {
          page,
          limit: limit || pageLimit,
          isDeleted: false,
          searchValue: debounceValue,
          searchFields: "name,frequency,planType",
        },
      });

      setPlansData(response?.data?.data);
      setPaginationData(response?.pagination);
    } catch (error) {
      console.log("Error : error", error.status);
      if (error.status === 401) {
        navigate("/");
      }
    } finally {
      setLoading({ isLoading: false, type: "getAllPlan" });
    }
  };

  const handleOpenModal = (type, item) => {
    setFormError({});
    const planData = {
      id: item?._id || null,
      name: item?.name || "",
      frequency: item?.frequency || "",
      pricePerQty: item?.pricePerQty || "",
      minimumQty: item?.minimumQty || "",
      planType: item?.planType || "",
    };

    setModalState({
      type,
      open: true,
      formData: planData,
      originalData: type !== "delete" ? planData : null,
      changedFields: {},
    });

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);

    setModalState({
      type: null,
      open: false,
      formData: {
        id: null,
        name: "",
        frequency: "",
      },
      changedFields: {},
    });
  };

  const handleInputChange = (field, value) => {
    setModalState((prevState) => {
      const isChanged = value !== prevState.originalData[field];

      return {
        ...prevState,
        formData: { ...prevState.formData, [field]: value },
        changedFields: { ...prevState.changedFields, [field]: isChanged },
      };
    });
  };

  const renderModalContent = () => {
    const isDisabled = !Object.values(modalState?.changedFields || {}).some(
      (field) => field
    );

    switch (modalState.type) {
      case "add":
        return (
          <ModalFormPlan
            type={"add"}
            data={modalState}
            handleCloseModal={handleCloseModal}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            loading={loading}
            formError={formError}
          />
        );
      case "editPlan":
        return (
          <ModalFormPlan
            type={"edit"}
            handleSubmit={handleSubmit}
            isDisabled={isDisabled}
            data={modalState}
            handleCloseModal={handleCloseModal}
            handleInputChange={handleInputChange}
            formError={formError}
            loading={loading}
          />
        );
      case "deletePlan":
        return (
          <DeleteModal
            handleSubmit={handleSubmit}
            handleCloseModal={handleCloseModal}
            modalName={"Plan"}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    const { type, formData, changedFields } = modalState;

    const error = PlanSchema({
      name: formData?.name,
      pricePerQty:
        formData?.planType === "complimentary" ? 1 : formData?.pricePerQty,
      frequency: formData?.frequency,
      minimumQty: formData?.minimumQty,
      planType: formData?.planType,
    });

    if (Object.keys(error).length !== 0 && type !== "deletePlan") {
      setFormError(error);
      return;
    }

    if (formData?.planType === "complimentary") formData.pricePerQty = 0;

    let url = "institute/plan";
    let config;

    switch (type) {
      case "add":
        {
          const { id, ...dataWithoutId } = formData;
          config = {
            url: url,
            method: "POST",
            data: dataWithoutId,
          };
        }
        break;
      case "editPlan":
        {
          const payload = {};

          for (const field in changedFields) {
            if (changedFields[field]) {
              payload[field] = formData[field];
            }
          }

          config = {
            url: `${url}/${formData.id}`,
            method: "PATCH",
            data: payload,
          };
        }
        break;
      case "deletePlan":
        config = {
          url: `${url}/${formData.id}`,
          method: "DELETE",
          data: { isDeleted: true },
        };
        break;
    }

    try {
      setLoading({ isLoading: true, type: "submit" });
      const response = await apiCall(config);

      getAllPlans();
      toast.success(
        response.status === "success" && type === "deletePlan"
          ? DELETED
          : response.message
      );
      handleCloseModal();
    } catch (err) {
      setLoading(false);
    } finally {
      setLoading({ isLoading: false, type: "" });
    }
  };

  const handlePageClick = (event) => {
    setSearchParams((prev) => {
      prev.set("currentPage", Number(event.selected) + 1);
      return prev;
    });
  };

  const handleSearchInputChange = (e) => {
    setSearch(e.target.value);
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mb: 2,
          mt: 2,
          p: 1,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          boxShadow: 2,
          backgroundColor: "#f9f9f9",
        }}
      >
        <TextField
          label="Search"
          name="name"
          className="cstmSearchBox"
          value={search}
          onChange={handleSearchInputChange}
          error={!!formError?.name}
          helperText={formError?.name}
          sx={{
            flexGrow: 1,
            mb: { xs: 2, sm: 0 },
            width: "100%",
            minWidth: { xs: "100%", sm: "300px" },
            maxWidth: "300px",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#1976d2",
              },
              "&:hover fieldset": {
                borderColor: "#1565c0",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#0d47a1",
              },
            },
          }}
        />

        <Button
          variant="contained"
          onClick={() => handleOpenModal("add")}
          sx={{
            flexShrink: 0,
            height: "45px",
            minWidth: { xs: "100%", sm: "120px" },
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Add Plan +
        </Button>
      </Box>

      <Box sx={{ height: 450 }}>
        {loading.isLoading && loading.type === "getAllPlan" ? (
          <Loader />
        ) : plansData?.length ? (
          <DataTable
            columns={adminInstitutePlanColumns}
            data={plansData}
            handleOpenModal={handleOpenModal}
          />
        ) : (
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              textAlign: "center",
              mt: 4,
            }}
          >
            No record found
          </Typography>
        )}
      </Box>

      <div className="pagin pagination-blog mb-3">
        <ReactPaginate
          onPageChange={handlePageClick}
          pageRangeDisplayed={3}
          pageCount={
            isNaN(paginationData.totalPages) ? 1 : paginationData.totalPages
          }
          forcePage={Number(paginationData.currentPage - 1)}
          renderOnZeroPageCount={null}
          previousLabel="< previous"
          nextLabel="next >"
          containerClassName="pagination"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          breakLabel="..."
          breakClassName="page-item"
          breakLinkClassName="page-link"
          activeClassName="active"
        />
      </div>

      <BaseModal
        classname="base_modal_mui CstmBaseModalWidth"
        open={openModal}
        handleClose={handleCloseModal}
        maxWidth={600}
      >
        {renderModalContent()}
      </BaseModal>
    </>
  );
};

export default PlanList;
